/**
 * Shop the Look — custom web components
 *
 * ShopTheLookCard  — triggers the shared drawer when clicked
 * ShopTheLookDrawer — manages the right-side panel, scroll lock, and focus trap
 */

/* ─────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────── */

/** All focusable elements inside a container */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  );
}

/** Simple focus trap: keep Tab / Shift+Tab inside `container` */
function trapFocus(container) {
  const focusable = getFocusableElements(container);
  if (!focusable.length) return () => {};

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  const handler = (event) => {
    if (event.key !== 'Tab') return;
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  container.addEventListener('keydown', handler);
  return () => container.removeEventListener('keydown', handler);
}

/* ─────────────────────────────────────────────────────────────────
   ShopTheLookCard
───────────────────────────────────────────────────────────────── */

class ShopTheLookCard extends HTMLElement {
  connectedCallback() {
    this.querySelector('.stl-card__btn')?.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.querySelector('.stl-card__btn')?.removeEventListener('click', this.#handleClick);
  }

  #handleClick = () => {
    const lookId = this.dataset.lookId;
    if (!lookId) return;

    /** Broadcast so any drawer on the page can respond */
    document.dispatchEvent(
      new CustomEvent('stl:open', { detail: { lookId }, bubbles: true })
    );
  };
}

/* ─────────────────────────────────────────────────────────────────
   ShopTheLookDrawer
───────────────────────────────────────────────────────────────── */

class ShopTheLookDrawer extends HTMLElement {
  /** @type {HTMLElement} */ #panel = null;
  /** @type {HTMLElement} */ #overlay = null;
  /** @type {HTMLElement} */ #content = null;
  /** @type {HTMLButtonElement} */ #closeBtn = null;
  /** @type {number} */ #scrollY = 0;
  /** @type {Function} */ #removeTrap = () => {};
  #isOpen = false;

  connectedCallback() {
    this.#panel   = this.querySelector('[ref="panel"]');
    this.#overlay = this.querySelector('[ref="overlay"]');
    this.#content = this.querySelector('[ref="content"]');
    this.#closeBtn = this.querySelector('[ref="closeBtn"]');

    this.#closeBtn?.addEventListener('click', () => this.close());
    this.#overlay?.addEventListener('click', () => this.close());

    document.addEventListener('stl:open', this.#handleOpen);

    document.addEventListener('keydown', (event) => {
      if (this.#isOpen && event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('stl:open', this.#handleOpen);
  }

  #handleOpen = (event) => {
    const { lookId } = event.detail;
    if (!lookId) return;

    const template = document.querySelector(`template[data-look-id="${lookId}"]`);
    if (!template) return;

    // Swap content
    this.#content.innerHTML = '';
    this.#content.appendChild(template.content.cloneNode(true));
    this.#content.scrollTop = 0;

    this.#open();
  };

  #open() {
    if (this.#isOpen) return;
    this.#isOpen = true;

    // Lock scroll (mirrors Horizon's dialog.js pattern)
    this.#scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top      = `-${this.#scrollY}px`;
    document.body.style.width    = '100%';

    // Show overlay
    if (this.#overlay) {
      this.#overlay.classList.add('stl-overlay--visible');
      // Trigger fade in next frame
      requestAnimationFrame(() => {
        this.#overlay.classList.add('stl-overlay--active');
      });
    }

    // Slide panel in (double rAF ensures browser has painted the hidden state first)
    if (this.#panel) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#panel.classList.add('stl-drawer--open');
          this.#panel.setAttribute('aria-hidden', 'false');

          // Set focus on close button
          this.#closeBtn?.focus();

          // Activate focus trap
          this.#removeTrap();
          this.#removeTrap = trapFocus(this.#panel);
        });
      });
    }
  }

  close() {
    if (!this.#isOpen) return;
    this.#isOpen = false;

    this.#removeTrap();

    // Fade overlay out
    this.#overlay?.classList.remove('stl-overlay--active');

    // Slide panel out then hide
    if (this.#panel) {
      this.#panel.classList.remove('stl-drawer--open');
      this.#panel.setAttribute('aria-hidden', 'true');

      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;

        if (this.#overlay) {
          this.#overlay.classList.remove('stl-overlay--visible');
        }

        // Restore scroll
        document.body.style.position = '';
        document.body.style.top      = '';
        document.body.style.width    = '';
        window.scrollTo({ top: this.#scrollY, behavior: 'instant' });
      };

      this.#panel.addEventListener('transitionend', finish, { once: true });
      // Fallback if transition never fires (reduced-motion, etc.)
      setTimeout(finish, 420);
    } else {
      // No panel element — restore immediately
      document.body.style.position = '';
      document.body.style.top      = '';
      document.body.style.width    = '';
      window.scrollTo({ top: this.#scrollY, behavior: 'instant' });
    }
  }
}

/* ─────────────────────────────────────────────────────────────────
   Mobile scroll-reveal for grid items
───────────────────────────────────────────────────────────────── */

function initScrollReveal() {
  const mq = window.matchMedia('(max-width: 749px)');
  if (!mq.matches) return;

  const items = document.querySelectorAll('.stl-grid__item');
  if (!items.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, i) => {
        if (!entry.isIntersecting) return;
        // Stagger sibling items that become visible together
        const delay = i * 80;
        setTimeout(() => entry.target.classList.add('stl-visible'), delay);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.12 }
  );

  items.forEach((item) => observer.observe(item));
}

/* ─────────────────────────────────────────────────────────────────
   Register
───────────────────────────────────────────────────────────────── */

if (!customElements.get('shop-the-look-card')) {
  customElements.define('shop-the-look-card', ShopTheLookCard);
}

if (!customElements.get('shop-the-look-drawer')) {
  customElements.define('shop-the-look-drawer', ShopTheLookDrawer);
}

/* ─────────────────────────────────────────────────────────────────
   Carousel scroll indicator
───────────────────────────────────────────────────────────────── */

function initScrollIndicator() {
  const carousel = document.querySelector('.stl-grid--carousel');
  const thumb = document.querySelector('.stl-scroll-thumb');
  if (!carousel || !thumb) return;

  function updateThumb() {
    const { scrollLeft, scrollWidth, clientWidth } = carousel;
    if (scrollWidth <= clientWidth) {
      thumb.style.width = '100%';
      thumb.style.left = '0%';
      return;
    }
    const widthRatio = clientWidth / scrollWidth;
    const thumbWidthPct = widthRatio * 100;
    const maxScroll = scrollWidth - clientWidth;
    const leftPct = (scrollLeft / maxScroll) * (100 - thumbWidthPct);
    thumb.style.width = thumbWidthPct + '%';
    thumb.style.left = leftPct + '%';
  }

  updateThumb();
  carousel.addEventListener('scroll', updateThumb, { passive: true });
  window.addEventListener('resize', updateThumb, { passive: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initScrollIndicator();
  });
} else {
  initScrollReveal();
  initScrollIndicator();
}
